import { ColorPartial, PaletteColor, PaletteOptions, TypeBackground } from '@material-ui/core/styles/createPalette';

/**
 * Core color palette for the College Hunks design system.
 * All colors are defined with accessibility in mind, following WCAG guidelines:
 * - Small text (below 24px): 4.5:1 contrast ratio
 * - Large text (above 24px): 3:1 contrast ratio
 * - Graphical elements: 3:1 contrast ratio
 */
export const colors = {
  /** Used as global background in light themes */
  white: '#ffffff',
  grey0: '#ffffff',
  /** Used for alternating rows and component backgrounds in internal products */
  grey10: '#F6F7F9',
  /** Used for table headers */
  grey20: '#ECEFF3',
  /** Used for most strokes around elements and borders */
  grey30: '#E3E7ED',
  grey40: '#DADFE8',
  grey60: '#C6CEDB',
  grey80: '#B3BDCE',
  grey100: '#A0ADC2',
  grey120: '#8691A6',
  /** Used for subheadings and light body text. Base grey for text on white backgrounds */
  grey140: '#6B7587',
  /** Used in dark theme for third layer components */
  grey160: '#505868',
  /** Used in dark theme for first layer components */
  grey170: '#404857',
  /** Used in dark theme for second layer components */
  grey180: '#323847',
  /** Used as global background in dark theme */
  grey190: '#0E0F16',
  grey600: '#181133',
  black: '#0E0F16',

  /**
   * Primary brand colors used for important interactive elements.
   * Used for buttons, CTAs, links, and other primary actions.
   */
  primary: {
    main: '#EA7200',
    light: '#F0CBA7',
    superlight: '#FDF2E8',
    dark: '#BF6006',
  },

  /**
   * Secondary brand colors used for waypoints and visual hierarchy.
   * Used for headers, categories, and other guideposts for users.
   */
  secondary: {
    main: '#226937',
    medium: '#61936D',
    light: '#E9F0EB',
    dark: '#1F5932',
  },

  /**
   * Basic colors for text and backgrounds.
   * Body text should use Black (#4A4A4A) for optimal readability.
   */
  basic: {
    white: '#FFFFFF',
    black: '#4A4A4A',
  },

  /**
   * Functional colors that give feedback on system or event status.
   * These colors have specific semantic meanings and should be used consistently.
   */
  functionals: {
    /** Used for error states and critical alerts */
    alertDark: '#D23A32',
    alert: '#E9554E',
    alertMedium: '#F29089',
    alertLight: '#FCEBEA',

    /** Used for disabled states and inactive elements */
    disabled: '#6B7587', // grey140
    disabledLight: '#DADFE8', // grey40

    /** Used for informational messages and help text */
    info: '#6798F8',
    infoLight: '#ECF2FE',
    infoSuperLight: '#F3F8FF',
    infoDark: '#4B75C9',
    infoUltraDark: '#4B75C9',
    bodyLight: '#6B7587',

    /** Used for success states and positive feedback */
    success: '#69D4A1',
    successMedium: '#C9F0DD',
    successLight: '#EBF9F3',
    successDark: '#337A59',

    /** Used for warning states and important notices */
    warning: '#E86825',
    warningLight: '#FFF1E0',
    warningDark: '#BA4709',
    warningUltraLight: '#FFF3E5',
  },

  /**
   * Complementary colors to bring more life into the designs.
   * These should be used sparingly and purposefully to enhance the visual experience.
   */
  complementary: {
    yellow: {
      shade01: '#E5B764',
      shade02: '#FFCB6F',
      shade03: '#FFE2AF',
    },
    red: {
      shade01: '#E57977',
      shade02: '#FF8684',
      shade03: '#FFDBDA',
      shade04: '#FFE7E6',
    },
    green: {
      shade01: '#51C9AF',
      shade02: '#5ADFC2',
    },
    blue: {
      shade01: '#5A8BE5',
      shade02: '#649AFF',
      shade03: '#D7E3F1',
      shade04: '#F3F8FF',
    },
    purple: {
      shade01: '#A044E7',
    },
    sand: {
      shade00: '#895700',
      shade01: '#B0760D',
      shade02: '#E5B764',
      shade04: '#FFF2DB',
    },
  },
} as const;

/**
 * Primary color configuration for Material-UI.
 * Uses the College Hunks orange as the main color with appropriate contrast text.
 */
const primary: PaletteColor = {
  main: colors.primary.main,
  light: colors.primary.light,
  dark: colors.primary.dark,
  contrastText: colors.basic.white,
};

/**
 * Secondary color configuration for Material-UI.
 * Uses the College Hunks green as the main color with appropriate contrast text.
 */
const secondary: PaletteColor = {
  main: colors.secondary.main,
  light: colors.secondary.light,
  dark: colors.secondary.dark,
  contrastText: colors.basic.white,
};

/**
 * Grey scale configuration for Material-UI.
 * Maps our custom grey scale to Material-UI's expected format.
 */
const grey: ColorPartial = {
  100: colors.grey10,
  200: colors.grey20,
  300: colors.grey30,
  400: colors.grey40,
  600: colors.grey60,
  800: colors.grey80,
  900: colors.grey100,
};

/**
 * Default background configuration for Material-UI.
 * Uses white as the default background color for light theme.
 */
const background: Partial<TypeBackground> = { default: colors.basic.white };

/**
 * Complete palette configuration for Material-UI.
 * Implements the College Hunks design system color palette with appropriate
 * contrast ratios and accessibility considerations.
 */
export const palette: PaletteOptions = {
  type: 'light',
  tonalOffset: 0.2,
  contrastThreshold: 3,
  primary: primary,
  secondary: secondary,
  grey: grey,

  text: {
    primary: colors.basic.black,
    secondary: colors.basic.black,
    disabled: colors.grey60,
  },

  divider: colors.grey30,

  common: {
    black: colors.basic.black,
    white: colors.basic.white,
  },

  background: background,

  action: {
    disabled: colors.grey60,
    disabledBackground: colors.grey60,
  },

  error: {
    main: colors.functionals.alert,
  },

  success: {
    main: colors.functionals.success,
    light: colors.functionals.successLight,
    dark: colors.functionals.successDark,
    contrastText: colors.basic.white,
  },

  warning: {
    main: colors.functionals.warning,
    light: colors.functionals.warningLight,
    dark: colors.functionals.warningDark,
    contrastText: colors.basic.white,
  },
};
